/**
 * conditions.js — Condition Evaluators (11 types)
 *
 * Evaluates whether an action's preconditions are met.
 * All conditions must pass for an action to be activatable.
 * Delegates to featureDetection.js for ancestry/class checks.
 *
 * Part of the Composable Mechanic Engine (Issue #82, Phase A: #174)
 */

import { hasAncestry, hasClass } from '../utils/featureDetection';
import { resolveExpression } from './expressions';

// ─── Condition Evaluators ───────────────────────────────────────────────────

const CONDITION_EVALUATORS = {
  /**
   * Check if the character has enough of a resource to pay a cost.
   * For stress: checks that marking `amount` more stress won't exceed maxStress.
   * For hope/hp: checks that current value >= amount.
   */
  resource_available: (condition, context) => {
    const { resource, amount = 1 } = condition;
    const { character } = context;

    if (resource === 'stress') {
      const stress = character?.stress || 0;
      const maxStress = character?.maxStress || 8;
      return (stress + amount <= maxStress)
        ? { passed: true, reason: '' }
        : { passed: false, reason: 'No Stress slots available' };
    }
    if (resource === 'hope') {
      const hope = character?.hope || 0;
      return (hope >= amount)
        ? { passed: true, reason: '' }
        : { passed: false, reason: `Need ${amount} Hope (have ${hope})` };
    }
    if (resource === 'hp') {
      const hp = character?.hp || 0;
      return (hp >= amount)
        ? { passed: true, reason: '' }
        : { passed: false, reason: `Need ${amount} HP (have ${hp})` };
    }
    return { passed: false, reason: `Unknown resource: ${resource}` };
  },

  /**
   * Check if the character has enough tokens of a specific type.
   */
  has_token: (condition, context) => {
    const { tokenId, resourceKey, minimum = 1 } = condition;
    const { character, cardKey } = context;
    const token = character?.cardResources?.[resourceKey || cardKey]?.[tokenId];
    const current = token?.current || 0;
    return (current >= minimum)
      ? { passed: true, reason: '' }
      : { passed: false, reason: `Need ${minimum} ${tokenId} (have ${current})` };
  },

  /**
   * Check that a session state key is NOT active (falsy).
   */
  not_active: (condition, context) => {
    const { stateKey } = condition;
    const { sessionState } = context;
    const isActive = sessionState?.[stateKey];
    return (!isActive)
      ? { passed: true, reason: '' }
      : { passed: false, reason: 'Already active' };
  },

  /**
   * Check that a session state key IS active (truthy).
   */
  is_active: (condition, context) => {
    const { stateKey } = condition;
    const { sessionState } = context;
    return (sessionState?.[stateKey])
      ? { passed: true, reason: '' }
      : { passed: false, reason: 'Not currently active' };
  },

  /**
   * Check that a cooldown has expired.
   */
  not_on_cooldown: (condition, context) => {
    const { cooldownKey } = condition;
    const { cooldowns = {} } = context;
    const cooldownEnd = cooldowns[cooldownKey];
    if (!cooldownEnd || Date.now() >= cooldownEnd) {
      return { passed: true, reason: '' };
    }
    return { passed: false, reason: 'On cooldown' };
  },

  /**
   * Check that character HP is below a threshold.
   * Threshold can be a static number or $ref expression.
   */
  hp_below: (condition, context) => {
    const { threshold } = condition;
    const { character } = context;
    const resolved = resolveExpression(threshold, context);
    return ((character?.hp || 0) < resolved)
      ? { passed: true, reason: '' }
      : { passed: false, reason: `HP not below ${resolved}` };
  },

  /**
   * Check that character level meets a minimum.
   */
  level_at_least: (condition, context) => {
    const { level } = condition;
    return ((context.character?.level || 1) >= level)
      ? { passed: true, reason: '' }
      : { passed: false, reason: `Requires level ${level}` };
  },

  /**
   * Check that a character-level boolean flag is truthy.
   */
  has_character_flag: (condition, context) => {
    const { flag } = condition;
    return (context.character?.[flag])
      ? { passed: true, reason: '' }
      : { passed: false, reason: `Missing: ${flag}` };
  },

  /**
   * Check that a character-level boolean flag is NOT set (falsy).
   * Used for toggle-style actions that can only activate when inactive.
   */
  not_has_character_flag: (condition, context) => {
    const { flag } = condition;
    return (!context.character?.[flag])
      ? { passed: true, reason: '' }
      : { passed: false, reason: 'Already active' };
  },

  /**
   * Check character ancestry. Delegates to featureDetection.js.
   * Handles mixed ancestries (e.g., "ribbet/firbolg").
   */
  has_ancestry: (condition, context) => {
    return hasAncestry(context.character, condition.ancestry)
      ? { passed: true, reason: '' }
      : { passed: false, reason: `Requires ${condition.ancestry} ancestry` };
  },

  /**
   * Check character class. Delegates to featureDetection.js.
   */
  has_class: (condition, context) => {
    return hasClass(context.character, condition.class)
      ? { passed: true, reason: '' }
      : { passed: false, reason: `Requires ${condition.class} class` };
  },
};

// ─── Public API ─────────────────────────────────────────────────────────────

/**
 * Evaluate a single condition against the current context.
 *
 * @param {object} condition - { type: string, ...params }
 * @param {object} context - { character, sessionState, cardKey, cooldowns }
 * @returns {{ passed: boolean, reason: string }}
 */
export const evaluateCondition = (condition, context) => {
  if (!condition || !condition.type) {
    return { passed: false, reason: 'Invalid condition (missing type)' };
  }

  const evaluator = CONDITION_EVALUATORS[condition.type];
  if (!evaluator) {
    return { passed: false, reason: `Unknown condition type: ${condition.type}` };
  }

  return evaluator(condition, context || {});
};

/**
 * Evaluate all conditions for an action. ALL must pass.
 *
 * @param {object[]} conditions - Array of condition objects
 * @param {object} context - { character, sessionState, cardKey, cooldowns }
 * @returns {{ canActivate: boolean, failedCondition: object|null, reason: string }}
 */
export const evaluateConditions = (conditions, context) => {
  if (!conditions || conditions.length === 0) {
    return { canActivate: true, failedCondition: null, reason: '' };
  }

  for (const condition of conditions) {
    const result = evaluateCondition(condition, context);
    if (!result.passed) {
      return {
        canActivate: false,
        failedCondition: condition,
        reason: result.reason,
      };
    }
  }

  return { canActivate: true, failedCondition: null, reason: '' };
};
